import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Code, PenLine, ChevronDown } from "lucide-react";
import { CONTACT } from "../../data/content";
import { buildSignatureHtml } from "../../lib/signature";

const WEBSITE = "www.nexsecure.in";

const GUIDES = [
  {
    id: "roundcube",
    title: "NexSecure Webmail (Roundcube)",
    steps: [
      "Click “Copy Signature” above.",
      "In webmail, go to Settings → Identities → click your email address.",
      "Tick the “HTML signature” box, then paste into the Signature field.",
      "Click Save — every new email will carry your signature.",
    ],
  },
  {
    id: "gmail",
    title: "Gmail",
    steps: [
      "Click “Copy Signature” above.",
      "Gmail → Settings (gear) → See all settings → General → Signature.",
      "Create a new signature, paste it in, and set it as default for new emails.",
    ],
  },
  {
    id: "outlook",
    title: "Outlook (desktop / web)",
    steps: [
      "Click “Copy Signature” above.",
      "Outlook → Settings → Mail → Compose and reply → Email signature.",
      "Paste, tick “Automatically include on new messages”, and Save.",
    ],
  },
];

export const AdminSignature = ({ user }) => {
  const [form, setForm] = useState({
    name: "",
    designation: "",
    mobile: "",
    email: "",
  });
  const [openGuide, setOpenGuide] = useState("roundcube");

  const html = useMemo(
    () =>
      buildSignatureHtml({
        name: form.name || "Your Name",
        designation: form.designation || "Your Designation",
        mobile: form.mobile,
        email: form.email || "you@nexsecure.in",
      }),
    [form]
  );

  const copyRich = async () => {
    try {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([`${form.name} — ${form.designation}\n${form.email}\n${CONTACT.phone}\nhttps://${WEBSITE}`], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
      toast.success("Signature copied — paste it into your mail settings");
    } catch {
      toast.error("Rich copy not available here — use “Copy HTML Code” instead");
    }
  };

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      toast.success("HTML source copied");
    } catch {
      toast.error("Copy failed — select the preview and copy manually");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input =
    "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
  const label = "block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2";

  return (
    <div data-testid="admin-signature" className="grid lg:grid-cols-2 gap-8 items-start">
      <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-8 min-w-0">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 flex items-center justify-center border border-nx-gold/40 bg-nx-gold/10">
            <PenLine size={17} className="text-nx-goldlight" />
          </span>
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Email Signature</p>
            <h2 className="text-lg text-white font-medium">Your details</h2>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="sig-name" className={label}>Full name</label>
            <input id="sig-name" className={input} placeholder="e.g. Asha Nair" value={form.name} onChange={set("name")} data-testid="signature-name-input" />
          </div>
          <div>
            <label htmlFor="sig-designation" className={label}>Designation</label>
            <input id="sig-designation" className={input} placeholder="e.g. Systems Engineer" value={form.designation} onChange={set("designation")} data-testid="signature-designation-input" />
          </div>
          <div>
            <label htmlFor="sig-mobile" className={label}>Mobile (optional)</label>
            <input id="sig-mobile" className={input} placeholder="e.g. +91 98XXXXXX00" value={form.mobile} onChange={set("mobile")} data-testid="signature-mobile-input" />
          </div>
          <div>
            <label htmlFor="sig-email" className={label}>Work email</label>
            <input id="sig-email" type="email" className={input} placeholder="you@nexsecure.in" value={form.email} onChange={set("email")} data-testid="signature-email-input" />
          </div>
        </div>
        <p className="mt-5 text-[11px] text-slate-600 leading-relaxed">
          Logo, services line, address, phone numbers and the legal disclaimer are fixed so every signature stays consistent.
        </p>
      </div>

      <div className="flex flex-col gap-6 min-w-0">
        <div className="border border-white/10 bg-white/[0.02] p-6 sm:p-8">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4">Live preview</p>
          <div className="bg-[#dfe3e8] rounded-sm p-4 sm:p-6 overflow-x-auto" data-testid="signature-preview">
            <div style={{ minWidth: 470 }} dangerouslySetInnerHTML={{ __html: html }} />
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={copyRich} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-xs tracking-wide px-6 py-3 transition-colors" data-testid="signature-copy-button">
              <Copy size={14} /> Copy Signature
            </button>
            <button onClick={copyHtml} className="flex items-center gap-2 text-xs tracking-wide text-slate-300 border border-white/20 hover:border-nx-gold/60 px-5 py-3 transition-colors" data-testid="signature-copy-html-button">
              <Code size={14} /> Copy HTML Code
            </button>
          </div>
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-6 sm:p-8" data-testid="signature-guides">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4">Where to paste it</p>
          <div className="flex flex-col gap-2">
            {GUIDES.map((g) => (
              <div key={g.id} className="border border-white/10">
                <button
                  onClick={() => setOpenGuide(openGuide === g.id ? null : g.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left text-sm text-slate-200 hover:text-nx-goldlight transition-colors"
                  data-testid={`signature-guide-${g.id}`}
                >
                  {g.title}
                  <ChevronDown size={14} className={`text-nx-goldlight transition-transform duration-300 ${openGuide === g.id ? "rotate-180" : ""}`} />
                </button>
                {openGuide === g.id && (
                  <ol className="px-5 pb-4 pt-1 list-decimal list-inside space-y-1.5 text-xs text-slate-400 leading-relaxed">
                    {g.steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
